import {AfterContentInit, Component, EventEmitter, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {combineLatest, Observable, of, Subscription}                             from 'rxjs';
import {ApGridPagerType, IApPermissionData, IGridPagerData, IWizardChoiceData}   from '../../../../ap-interface';
import {ApPermissions}                                                           from 'invoker-transport';
import {
  ApDynComponentComponent
}                                                                                from '../../../../ap-dyncomponent/ap-dyncomponent.component';
import {
  ApDynGridColumnConfigBase
}                                                                                from '../../../../ap-dyngrids/config/ap-dyn-grid-column-config-base';
import {
  ApDynGridPagerConfigBase
}                                                                                from '../../../../ap-dyngrids/config/ap-dyn-grid-pager-config-base';
import {GetBooleanPipe}                                                          from '../../../../ap-utils';
import {
  ApGetAvailableUserGroupsPipe
}                                                                                from '../../../../stores/pipes/ap-get-available-usergroups.pipe';
import {
  ApDynGridStatusColumnConfig
}                                                                                from '../../../../ap-dyngrids/config/ap-dyn-grid-status-column-config';
import {
  ApDynGridPropertyColumnConfig
}                                                                                from '../../../../ap-dyngrids/config/ap-dyn-grid-property-column-config';
import {
  ApDynGridEditColumnConfig
}                                                                                from '../../../../ap-dyngrids/config/ap-dyn-grid-edit-column-config';
import {
  ApDynGridPagerConfig
}                                                                                from '../../../../ap-dyngrids/config/ap-dyn-grid-pager-config';
import {
  ApUsersInvitationEditComponent
}                                                                                from '../../../../entry-components/ap-basedata-entry-components/ap-users-invitation-edit.component';
import {
  InvitationStore
}                                                                                from '../../../../stores/login/invitation.store';
import {
  FormStore
}                                                                                from '../../../../stores/layout/form.store';
import {
  MapViewStore
}                                                                                from '../../../../stores/layout/mapview.store';
import {
  TranslationStore
}                                                                                from '../../../../stores/translation/translation.store';
import {
  GetPermissionPipe
}                                                                                from '../../../../ap-permission/pipes/get-permission.pipe';
import {
  LanguageStore
}                                                                                from '../../../../stores/translation/language.store';
import {
  ApUtilService
}                                                                                from '../../../../ap-utils/service/ap-util.service';
import {
  NotifyStore
}                                                                                from '../../../../stores/dialog/notify.store';
import {
  ApDateService
}                                                                                from '../../../../ap-core/services/ap-date-service';
import {map}                                                                     from 'rxjs/operators';
import IApValidationResult = Data.Api.Validation.IApValidationResult;
import IInvitations = Data.Authentication.IInvitations;

enum UserInvitations {
  Remove = 0
}

enum UserInvitationsStatus {
  Waiting = 0,
  Activated = 1,
  Expired = 2
}

@Component({
  selector: 'ap-users-invitation',
  template: `
      <ap-dyncomponent [columns]="columns"
                       [items]="(items$ | async)"
                       [loading$]="invitationsLoading$"
                       [pager]="pager"
                       [groupable]="true"
                       [canSearch]="true"
                       (create)="onCreate()"
                       [canCreate]="(ApPermissions.EDIT_USERS | getPermission | async)">
      </ap-dyncomponent>`
})
export class ApUsersInvitationComponent implements OnInit, AfterContentInit, OnDestroy {
  @ViewChild(ApDynComponentComponent, {static: true}) dynComponent: ApDynComponentComponent;
  public items$: Observable<any> = of([]);
  public columns: ApDynGridColumnConfigBase[] = [];
  public pager: ApDynGridPagerConfigBase;
  public invitationsLoading$: Observable<boolean>;
  public ApPermissions = ApPermissions;
  public apPermissionData: IApPermissionData;

  private _listMassEditing: any[] = [];
  private _subscriptions: Array<Subscription> = [];
  private _applyClicked: EventEmitter<any> = new EventEmitter<any>();
  private _listUserGroupItems: Array<{ text: string, value: number }> = [];

  constructor(private invitationStore: InvitationStore,
              private mapViewStore: MapViewStore,
              private formStore: FormStore,
              private translationStore: TranslationStore,
              private languageStore: LanguageStore,
              private notifyStore: NotifyStore,
              private dateService: ApDateService,
              private permissionPipe: GetPermissionPipe,
              private booleanPipe: GetBooleanPipe,
              private getAvailableUserGroups: ApGetAvailableUserGroupsPipe) {
  }

  ngOnInit(): void {
    this.mapViewStore.hideMapView();
  }

  ngAfterContentInit(): void {
    this._gridBuilder();
    this._listUserGroupItems = this.getAvailableUserGroups.transform();
    this.invitationsLoading$ = this.invitationStore.Listen(s => s.loading);
    this.invitationStore.loadInvitations();
    this._getItems();
    this._fillWizardChoiceData();
    this._subscriptions.push(this._applyClicked.subscribe((e) => this._onApplyClicked(e)));
    this._subscriptions.push(this.invitationStore.Listen(x => x.isInvitationResend)
      .subscribe(resendResult => {
          if (resendResult === undefined) {
            return;
          }
          const notification = {
            DisplayTimeout: 5
          } as IApValidationResult;
          if (resendResult) {
            notification.Level = Data.Api.Validation.ApValidationLevel.Success;
            notification.ErrorKey = 'Global__Invitations_Resend_Successfully';
          } else {
            notification.Level = Data.Api.Validation.ApValidationLevel.Error;
            notification.ErrorKey = 'Global__Invitations_Resend_Fail';
          }
          this.notifyStore.addMessage(notification);
        }
      ));
  }

  ngOnDestroy(): void {
    this._subscriptions.forEach(d => d.unsubscribe());
  }

  public onCreate(): void {
    setTimeout(() => this.formStore.openForm({component: ApUsersInvitationEditComponent, data: null}), 1);
  }

  private _getItems(): void {
    this.items$ = combineLatest([
      this.invitationStore.Listen(s => s.data)
    ]).pipe(
      map(([invitations]) => {
        return invitations.map((invitation: IInvitations): any => {
          const status = this._getStatus(invitation);
          const userGroups = this._getUserGroups(invitation.Usergroupid);
          return {
            Id: invitation.Id,
            SourceItem: invitation,
            UserGroups: userGroups,
            Status: status,
            StatusClass: this._getStatusClass(status),
            StatusDesc: this._getStatusDescription(status),
            Date: invitation.Created ? this.dateService.dateToMoment(invitation.Created).format('DD.MM.YYYY') : null,
            IsDriver: this._boolean(invitation.Isdriver),
          };
        });
      }));
  }

  private _fillWizardChoiceData(): void {
    const remove = {
      id: UserInvitations.Remove,
      name: this.translationStore.FindTranslationForSelectedLanguage('Global__Delete'),
      type: ApGridPagerType.None,
      values: null,
    } as IWizardChoiceData;
    this._listMassEditing.push(remove);
  }

  private _gridBuilder(): void {
    this.languageStore.SelectedLanguage$.subscribe(l => {
      this.columns = [
        new ApDynGridStatusColumnConfig({
          field: 'Status',
          showHeaderTitle: true,
          class: 'StatusClass',
          description: 'StatusDesc',
          filterable: true,
          sortIndex: 0,
          sortDesc: true,
          groupIndex: 0,
          groupDesc: true
        }),
        new ApDynGridPropertyColumnConfig({
          field: 'SourceItem.Email',
          title: 'Global__Email',
          filterable: true,
          width: 120,
          headerFilterable: true,
        }),
        new ApDynGridPropertyColumnConfig({
          field: 'Date',
          title: 'Global__CreatedAt',
          filterable: true,
          width: 120,
          headerFilterable: true,
          format: l.DateFormat
        }),
        new ApDynGridPropertyColumnConfig({
          field: 'UserGroups',
          title: 'Global_User_Group',
          filterable: true,
          width: 120,
          headerFilterable: true,
        }),
        new ApDynGridPropertyColumnConfig({
          field: 'IsDriver',
          title: 'Global__Worker',
          filterable: true,
          width: 50,
          headerFilterable: true,
          checkbox: true,
        }),
        new ApDynGridEditColumnConfig(
          {
            data: [
              {
                text: 'Global__Invitations_Resend',
                callback: ApUtilService.createEventEmitter(this._resendInvitation.bind(this), this._subscriptions),
                disabled: (dataItem: any): boolean => dataItem.Status === UserInvitationsStatus.Activated
              }
            ]
          }
        )
      ];
      this.permissionPipe.transform(ApPermissions.EDIT_USERS).subscribe(permission => {
        this.pager = new ApDynGridPagerConfig({
          listMassEditing: this._listMassEditing,
          apply: this._applyClicked,
          hasPermission: permission,
        });
      });
    });
  }

  private _resendInvitation(dataItem: any): void {
    this.invitationStore.resend(dataItem.SourceItem.Email);
  }

  private _onApplyClicked(data: IGridPagerData): void {
    const rows = this.dynComponent.getSelectedItems();
    rows.forEach(row => {
      if (data.object.id === UserInvitations.Remove) {
        this.invitationStore.delete(row as IInvitations);
      }
    });
  }

  private _getUserGroups(item: number): string {
    const group = this._listUserGroupItems.Find(_ => _?.value === item);
    return group !== undefined ? this.translationStore.FindTranslationForSelectedLanguage(group?.text) : '';
  }

  private _boolean(value: any): boolean {
    return this.booleanPipe.transform(value);
  }

  private _getStatus(invitation: IInvitations): UserInvitationsStatus {
    if (invitation.Confirmation_Time) {
      return UserInvitationsStatus.Activated;
    }
    if (invitation.Created) {
      const invitationDate = new Date(invitation.Created);
      invitationDate.setDate(invitationDate.getDate() + 7);
      if (invitationDate > this.dateService.getUtc()) {
        return UserInvitationsStatus.Waiting;
      }
    }
    return UserInvitationsStatus.Expired;
  }

  private _getStatusClass(status: UserInvitationsStatus): string {
    switch (status) {
      case UserInvitationsStatus.Waiting:
        return 'ap-status-yellow';
      case UserInvitationsStatus.Activated:
        return 'ap-status-green';
      case UserInvitationsStatus.Expired:
        return 'ap-status-red';
    }
  }

  private _getStatusDescription(status: UserInvitationsStatus): string {
    switch (status) {
      case UserInvitationsStatus.Waiting:
        return 'Global__Invitations_Waiting';
      case UserInvitationsStatus.Activated:
        return 'Global__Invitations_Activated';
      case UserInvitationsStatus.Expired:
        return 'Global__Invitations_Expired';
    }
  }
}
